import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../services/auth'
import { UserRole, AuctionWithItems } from '../types'
import ThemeToggle from './ThemeToggle'
import NotificationCenter from './NotificationCenter'
import AddPaymentMethodModal from './AddPaymentMethodModal'
import SaveAuctionButton from './SaveAuctionButton'
import { apiService } from '../services/api'

interface HeaderProps {
  onMenuToggle?: () => void
  showMenuToggle?: boolean
  auction?: AuctionWithItems
}

export default function Header({ onMenuToggle, showMenuToggle = true, auction }: HeaderProps) {
  const { user, logout } = useAuthStore()
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const [hasPaymentMethods, setHasPaymentMethods] = useState(true)
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)



  // Check payment methods when user logs in
  useEffect(() => {
    if (user && user.role === UserRole.BIDDER) {
      checkPaymentMethods()
    }
  }, [user])

  // Function to check payment methods
  const checkPaymentMethods = async () => {
    try {
      const hasPayment = await apiService.hasPaymentMethods()
      setHasPaymentMethods(hasPayment)
    } catch (error) {
      // Error checking payment methods - silently continue
      setHasPaymentMethods(true) // Default to true to avoid showing button on error
    }
  }



  const handleLogout = async () => {
    await logout()
    window.location.href = '/'
  }

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu)
  }

  return (
    // Sticks to the top of the viewport, or flush under the impersonation
    // banner while one is shown - the custom property is 0px otherwise.
    <header
      className="sticky z-50 bg-white dark:bg-dark-secondary shadow-sm border-b border-gray-200 dark:border-dark-primary"
      style={{ top: 'var(--impersonation-banner-height)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            {/* Mobile menu button */}
            {user && showMenuToggle && (
              <button
                onClick={onMenuToggle}
                className="md:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-tertiary mr-3 transition-colors"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Home button for live auction pages */}
            {user && !showMenuToggle && (
              <Link
                to="/"
                className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-tertiary mr-3 transition-colors"
                aria-label="Go home"
                title="Home"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </Link>
            )}
            

          </div>

          <nav className="hidden md:flex space-x-8">
          </nav>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Save Auction Button - Show on auction pages */}
            {auction && <SaveAuctionButton auction={auction} showText={false} />}
            <ThemeToggle />
            {user && <NotificationCenter />}
            {/* Add Payment Method Button - Show for bidders without payment methods */}
            {user && user.role === UserRole.BIDDER && !hasPaymentMethods && (
              <button
                onClick={() => setShowAddPaymentModal(true)}
                className="btn btn-primary text-sm px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 animate-pulse"
                title="Add a payment method to participate in auctions"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Add Payment Method
              </button>
            )}
            {user ? (
              <>
                {/* Settings Button */}
                <Link
                  to="/settings"
                  className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
                  aria-label="Settings"
                  title="Settings"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </Link>

                {/* Desktop user info */}
                <div className="hidden sm:flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate max-w-32">
                      {user.displayName || user.email}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {user.role === UserRole.AUCTIONEER ? 'Auctioneer' : 
                       user.role === UserRole.SYSTEM_ADMIN ? 'System Admin' :
                       user.role === UserRole.STAFF ? 'Staff' : 'Bidder'}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="btn btn-secondary text-sm"
                  >
                    Sign Out
                  </button>
                </div>
                
                {/* Mobile user menu */}
                <div className="sm:hidden relative">
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
                    aria-label="User menu"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </button>
                  
                  {showMobileMenu && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowMobileMenu(false)}
                      />
                      
                      {/* Mobile menu */}
                      <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-dark-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-primary z-50">
                        <div className="p-4 border-b border-gray-200 dark:border-dark-primary">
                          <div className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate">
                            {user.displayName || user.email}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {user.role === UserRole.AUCTIONEER ? 'Auctioneer' : 
                             user.role === UserRole.SYSTEM_ADMIN ? 'System Admin' :
                             user.role === UserRole.STAFF ? 'Staff' : 'Bidder'}
                          </div>
                        </div>
                        <div className="p-2">
                          <Link
                            to="/settings"
                            onClick={() => setShowMobileMenu(false)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary rounded-md transition-colors flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Settings
                          </Link>
                          <button
                            onClick={() => {
                              handleLogout()
                              setShowMobileMenu(false)
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-tertiary rounded-md transition-colors flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Sign Out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Link 
                  to="/signin" 
                  className="text-sm sm:text-base text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
                >
                  Sign In
                </Link>
                <Link to="/signup" className="btn btn-primary text-sm">
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onSuccess={() => {
          setShowAddPaymentModal(false)
          checkPaymentMethods() // Refresh payment method status
        }}
        title="Add Your First Payment Method"
        description="Add a payment method to participate in auctions, place bids, and make donations."
      />
    </header>
  )
}
